import { useEffect } from "react";
import { useDispatch } from "react-redux";

import { useIntl } from "react-intl";

import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";

import EmailContext from "@app/component/email/creation/EmailContext";
import { deleteEmailDraft } from "@app/store/email/actions";
import useEmailDraftInfoGetter from "@app/util/email/useEmailDraftInfoGetter";

import useEmailDraftSave from "./useEmailDraftSave";

const DRAFT_SAVE_DEBOUNCE_TIMEOUT_MS = 1000 * 2;

const useEmailDraft = () => {
  const intl = useIntl();

  const dispatch = useDispatch();
  const draftInfoGetter = useEmailDraftInfoGetter();

  const {
    attachedFiles,
    body,
    draftId,
    emailTouched,
    entities,
    onDraftChange,
    resources,
    sent,
    subject,
  } = useNonNullableContext(EmailContext);

  const saveDraft = useEmailDraftSave();

  const saveDraftDebounced = useDebouncedCallback(
    [saveDraft, DRAFT_SAVE_DEBOUNCE_TIMEOUT_MS],
    [dispatch]
  );

  useEffect(() => {
    if (emailTouched) {
      saveDraftDebounced();
    }
  }, [body, entities, resources, subject, emailTouched, sent, saveDraftDebounced, attachedFiles]);

  useEffect(() => {
    if (sent && draftId) {
      const { draft } = draftInfoGetter(draftId, intl)!;
      dispatch(deleteEmailDraft.request({ draft }));
      onDraftChange(undefined);
    }
  }, [sent, draftId, onDraftChange, dispatch, draftInfoGetter, intl]);

  useEffect(() => {
    return () => {
      if (!sent && draftId) {
        saveDraft();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useEmailDraft;
